/**
 * Service pour exporter un template de prise de besoin en Excel
 * Génère un fichier Excel structuré avec toutes les questions par phase + métier
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getIntakeTemplate } from '../domain/besoins/templates';
import { getMetierFormConfig } from '../domain/metier/metierTypes';

/**
 * Exporte un template de prise de besoin en Excel
 */
export async function exportTemplateToExcel(templateId: string, metierId: string): Promise<void> {
  // Récupérer le template
  const template = getIntakeTemplate(templateId);
  if (!template) {
    throw new Error(`Template ${templateId} non trouvé`);
  }

  // Récupérer la config métier
  const metierConfig = getMetierFormConfig(metierId);
  if (!metierConfig) {
    throw new Error(`Métier ${metierId} non trouvé`);
  }

  // Créer un nouveau workbook
  const workbook = XLSX.utils.book_new();

  // ========== FEUILLE 1 : INFORMATIONS GÉNÉRALES ==========
  const infoData = [
    ['TEMPLATE DE PRISE DE BESOIN PRIsME'],
    [],
    ['Template:', template.name],
    ['Version:', template.version],
    ['Type de document:', template.documentTypeCode],
    ['Métier:', metierConfig.titre],
    [],
    ['Instructions:'],
    ['1. Remplissez toutes les sections de ce fichier'],
    ['2. Les questions marquées (*) sont obligatoires'],
    ['3. Pour les questions à choix multiples, cochez les options pertinentes'],
    ['4. Retournez ce fichier complété pour génération des documents PRIsME'],
    [],
    ['Date de complétion: _______________'],
    ['Nom du client: _______________'],
    ['Personne contact: _______________'],
  ];

  const infoSheet = XLSX.utils.aoa_to_sheet(infoData);
  
  // Styling (largeur colonnes)
  infoSheet['!cols'] = [{ wch: 60 }];
  
  XLSX.utils.book_append_sheet(workbook, infoSheet, 'Informations');

  // ========== FEUILLES PAR PHASE ==========
  template.phases.forEach((phase, phaseIndex) => {
    const phaseData: any[][] = [
      [`PHASE ${phaseIndex + 1} : ${phase.title.toUpperCase()}`],
      [],
    ];

    if (phase.description) {
      phaseData.push([phase.description]);
      phaseData.push([]);
    }

    // En-têtes
    phaseData.push(['Question', 'Réponse', 'Notes']);

    // Questions
    phase.questions.forEach((question) => {
      const questionLabel = question.required ? `${question.label} *` : question.label;
      
      // Ligne question
      phaseData.push([questionLabel, '', '']);

      // Si c'est un select ou multiselect, ajouter les options
      if (question.type === 'select' && question.options) {
        question.options.forEach((option) => {
          phaseData.push(['', `☐ ${option}`, '']);
        });
        phaseData.push([]); // Ligne vide après les options
      } else if (question.type === 'multiselect' && question.options) {
        question.options.forEach((option) => {
          phaseData.push(['', `☐ ${option}`, '']);
        });
        if (question.maxSelections) {
          phaseData.push(['', `(Maximum ${question.maxSelections} sélections)`, '']);
        }
        phaseData.push([]); // Ligne vide après les options
      } else if (question.type === 'textarea') {
        // Pour textarea, ajouter plusieurs lignes vides
        phaseData.push(['', '', '']);
        phaseData.push(['', '', '']);
        phaseData.push(['', '', '']);
      } else {
        // Pour text, une ligne vide
        phaseData.push([]);
      }

      // Help text si présent
      if (question.helpText) {
        phaseData.push(['', `ℹ️ ${question.helpText}`, '']);
        phaseData.push([]);
      }
    });

    // Créer la feuille
    const sheet = XLSX.utils.aoa_to_sheet(phaseData);
    
    // Largeur des colonnes
    sheet['!cols'] = [
      { wch: 40 }, // Question
      { wch: 30 }, // Réponse
      { wch: 30 }, // Notes
    ];

    // Fusionner la première ligne (titre)
    if (!sheet['!merges']) sheet['!merges'] = [];
    sheet['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: 2 },
    });

    // Nom de la feuille (max 31 caractères pour Excel)
    const sheetName = `Phase ${phaseIndex + 1}`.substring(0, 31);
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  });

  // ========== FEUILLE MÉTIER ==========
  if (metierConfig && metierConfig.fields.length > 0) {
    const metierData: any[][] = [
      ['PHASE 5 : PRISE DE BESOIN MÉTIER'],
      [],
      [`Métier: ${metierConfig.titre}`],
      [],
      [metierConfig.description],
      [],
      ['Question', 'Réponse', 'Notes'],
    ];

    metierConfig.fields.forEach((field) => {
      const fieldLabel = field.required ? `${field.label} *` : field.label;
      
      // Ligne question
      metierData.push([fieldLabel, '', '']);

      // Si c'est un select ou multiselect, ajouter les options
      if (field.type === 'select' && field.options) {
        field.options.forEach((option) => {
          metierData.push(['', `☐ ${option}`, '']);
        });
        metierData.push([]);
      } else if (field.type === 'multiselect' && field.options) {
        field.options.forEach((option) => {
          metierData.push(['', `☐ ${option}`, '']);
        });
        if (field.maxSelections) {
          metierData.push(['', `(Maximum ${field.maxSelections} sélections)`, '']);
        }
        metierData.push([]);
      } else if (field.type === 'textarea' || field.type === 'multiline_list') {
        // Pour textarea/multiline, ajouter plusieurs lignes vides
        metierData.push(['', '', '']);
        metierData.push(['', '', '']);
        metierData.push(['', '', '']);
      } else {
        metierData.push([]);
      }

      // Help text si présent
      if (field.helpText) {
        metierData.push(['', `ℹ️ ${field.helpText}`, '']);
        metierData.push([]);
      }
    });

    const metierSheet = XLSX.utils.aoa_to_sheet(metierData);
    
    // Largeur des colonnes
    metierSheet['!cols'] = [
      { wch: 40 },
      { wch: 30 },
      { wch: 30 },
    ];

    // Fusionner la première ligne
    if (!metierSheet['!merges']) metierSheet['!merges'] = [];
    metierSheet['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: 2 },
    });

    XLSX.utils.book_append_sheet(workbook, metierSheet, 'Métier');
  }

  // ========== GÉNÉRER ET TÉLÉCHARGER ==========
  // Générer le fichier Excel
  const excelBuffer = XLSX.write(workbook, {
    type: 'array',
    bookType: 'xlsx',
    cellStyles: true,
  });

  // Créer un blob et télécharger
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  // Nom du fichier
  const fileName = `PRIsME_${template.name.replace(/\s+/g, '_')}_${metierConfig.titre.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  
  saveAs(blob, fileName);
}










